import { useState } from 'react';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import { BaseAnalyticsFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import { UserRankingDto } from '@repo/shared/dtos/analytics/UtilizationDto';
import { AnalyticsModal } from '@repo/ui/components/Modal/AnalyticsModal';
import { User } from 'lucide-react';
import { useSession } from '../../../../hooks/auth/useSession';
import { FilterSummary } from '../shared/FilterSummary';
import { ExportButton } from '../../../../utils/export';
import { ExportFormat } from '../../../../utils/export/types';
import { useUserUtilization } from '../../../../hooks/analytics/useUserUtilization';
import { UserStats } from './UserStats';
import { UserSessionTrendChart } from './UserSessionTrendChart';
import { MonthExperienceBreakdown } from './MonthExperienceBreakdown';

interface UserDetailModalProps {
  user: UserRankingDto;
  isOpen: boolean;
  onClose: () => void;
  filters?: BaseAnalyticsFilter;
}

export function UserDetailModal({ user, isOpen, onClose, filters }: UserDetailModalProps) {
  const isMobile = useIsMobile();
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const { session } = useSession();
  const isLumenaAdmin = !!session?.isglobal;

  if (!isLumenaAdmin) {
    return null;
  }

  const {
    data: detailedUserData,
    isLoading,
    error,
  } = useUserUtilization({
    firstName: user.first_name,
    lastName: user.last_name,
    filters,
    enabled: isOpen,
  });

  const userToDisplay = detailedUserData || user;

  const exportData = [
    {
      firstName: userToDisplay.first_name,
      lastName: userToDisplay.last_name,
      fullName: `${userToDisplay.first_name} ${userToDisplay.last_name}`,
      userType: userToDisplay.user_type,
      totalSessions: userToDisplay.total_sessions,
      totalMinutes: Math.round(userToDisplay.total_minutes),
      sessionsThisMonth: userToDisplay.sessions_this_month,
      minutesThisMonth: Math.round(userToDisplay.minutes_this_month),
      averageSessionDuration:
        userToDisplay.total_sessions > 0
          ? Math.round(userToDisplay.total_minutes / userToDisplay.total_sessions)
          : 0,
      mostUsedExperience: userToDisplay.most_used_experience || 'N/A',
      lastSessionDate: userToDisplay.last_session_date,
      daysSinceLastSession: userToDisplay.days_since_last_session,
      experiencesUsed: detailedUserData?.experiences_used?.join(', ') || 'N/A',
    },
  ];

  const handleClose = () => {
    setSelectedMonth(null);
    onClose();
  };

  return (
    <AnalyticsModal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        <span className="flex items-center gap-2">
          <User className="h-6 w-6" />
          {`${userToDisplay.first_name} ${userToDisplay.last_name}`}
        </span>
      }
      className="max-w-6xl"
      headerContent={
        <ExportButton
          data={exportData}
          config={{
            fileName: `user-details-${userToDisplay.first_name}-${userToDisplay.last_name}`
              .toLowerCase()
              .replace(/\s+/g, '-'),
            date: true,
          }}
          format={ExportFormat.CSV}
          className="text-[var(--anl-text-muted)] transition-colors hover:text-[var(--anl-text)]"
        />
      }
    >
      <div className={isMobile ? 'p-3' : 'p-6'}>
        {isLoading && (
          <>
            {/* Stats skeleton — 4 cards matching UserStats layout */}
            <div className={`mb-6 grid gap-4 ${isMobile ? 'grid-cols-2' : 'grid-cols-4'}`}>
              {[0, 1, 2, 3].map(i => (
                <div
                  key={i}
                  className="rounded-lg border border-[var(--anl-border)] bg-[var(--anl-surface-elev)] p-4"
                >
                  <div className="mb-2 flex items-center space-x-2">
                    <div className="h-5 w-5 animate-pulse rounded bg-[var(--anl-border)]" />
                    <div className="h-3 w-20 animate-pulse rounded bg-[var(--anl-border)]" />
                  </div>
                  <div className="h-6 w-12 animate-pulse rounded bg-[var(--anl-border)]" />
                </div>
              ))}
            </div>
            {/* Chart skeleton — matches UserSessionTrendChart card */}
            <div className="rounded-lg border border-[var(--anl-border)] bg-[var(--anl-surface)]">
              <div className="flex items-center justify-between border-b border-[var(--anl-border)] p-4">
                <div className="h-5 w-40 animate-pulse rounded bg-[var(--anl-border)]" />
                <div className="h-4 w-32 animate-pulse rounded bg-[var(--anl-border)]" />
              </div>
              <div className="flex items-end gap-2 px-8 pb-8 pt-6" style={{ height: 240 }}>
                {[60, 80, 45, 90, 70, 55, 85, 40, 75, 65, 50, 95].map((h, i) => (
                  <div
                    key={i}
                    className="flex-1 animate-pulse rounded-t bg-[var(--anl-surface-elev)]"
                    style={{ height: `${h}%` }}
                  />
                ))}
              </div>
            </div>
          </>
        )}

        {error && (
          <div className="mb-6 rounded-lg border border-[var(--anl-border)] bg-[var(--anl-surface-elev)] p-4">
            <div className="flex items-center space-x-2">
              <div className="text-red-500">⚠️</div>
              <span className="text-[var(--anl-text)]">
                Failed to load detailed user data. Showing basic information.
              </span>
            </div>
          </div>
        )}

        {!isLoading && (
          <>
            <FilterSummary filters={filters} />

            <UserStats user={userToDisplay} />

            {detailedUserData?.monthly_session_history &&
              detailedUserData.monthly_session_history.length > 0 && (
                <div className="mb-6">
                  <UserSessionTrendChart
                    data={detailedUserData.monthly_session_history}
                    onBarClick={monthYear =>
                      setSelectedMonth(prev => (prev === monthYear ? null : monthYear))
                    }
                    selectedMonth={selectedMonth}
                  />
                </div>
              )}

            {selectedMonth && (
              <MonthExperienceBreakdown
                firstName={user.first_name}
                lastName={user.last_name}
                monthYear={selectedMonth}
                filters={filters}
                onClear={() => setSelectedMonth(null)}
              />
            )}
          </>
        )}
      </div>
    </AnalyticsModal>
  );
}
